import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { supabase } from '@/lib/supabase'
import type { User } from '@supabase/supabase-js'
import { useRouter } from 'vue-router'
import { type Database } from '../../shared/db/public.types'
import { showSuccess, showError } from '@/utils/toast'
import { loadActiveAccountId, persistActiveAccountId, resolveActiveAccountId } from '@/utils/accountselection'

export type Account = Database['public']['Tables']['accounts']['Row'];
export type AccountUser = Database['public']['Tables']['account_users']['Row'] & {
  account?:Account
};

export interface AuthUser {
  id: string
  email: string
  displayName?: string
  avatarUrl?: string
  provider: string
  providerId: string
}

// Role type definitions
export type UserRole = 'admin' | 'dev' | 'member'
export type AccountUserRole = 'admin' | 'dev' | 'botuser'

// Role display names for UI
export const ROLE_DISPLAY_NAMES: Record<UserRole, string> = {
  admin: 'Administrator',
  dev: 'Developer', 
  member: 'Member'
}


export const useAuthStore = defineStore('auth', () => {
  const user = ref<AuthUser | null>(null)
  const activeAccountId = ref<string | null>(null)
  const account = ref<Account | null>(null)
  const accountUsers = ref<AccountUser[]>([])
  const session = ref<any>(null)
  const router = useRouter()

  const loading = ref(false)
  const error = ref<string | null>(null)
  const initialized = ref(false)
  let initializationPromise: Promise<void> | null = null
  let avatarRefreshPromise: Promise<void> | null = null

  const resetAuthState = () => {
    user.value = null
    activeAccountId.value = null
    account.value = null
    accountUsers.value = []
    session.value = null
    clearPersistedAuth()
  }

  const handleMissingSession = async () => {
    console.log('AuthStore: Missing or expired session detected, resetting auth state')
    resetAuthState()

    if (router.currentRoute.value.path.startsWith('/dashboard')) {
      console.log('AuthStore: Redirecting to sign-in from protected route')
      await router.push('/signin')
    }
  }

  const loadPersistedAccountSelection = () => {
    activeAccountId.value = loadActiveAccountId()
  }

  const persistAccountSelection = () => {
    persistActiveAccountId(activeAccountId.value)
  }

  const isIpAddress = (hostname: string) => /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname)

  const shouldUseCookieDomain = (hostname: string) => {
    return hostname !== 'localhost' && hostname !== '127.0.0.1' && hostname !== '::1' && !isIpAddress(hostname)
  }

  const appendCookieScope = (cookieStr: string) => {
    if (!shouldUseCookieDomain(window.location.hostname)) return cookieStr
    const domain = window.location.hostname.includes('radsuite.com') ? '.radsuite.com' : window.location.hostname
    return `${cookieStr}; domain=${domain}; Secure`
  }

  // Persist auth state to localStorage and cookies
  const persistAuth = () => {
    try {
      const session = supabase.auth.getSession();
      session.then(({ data: { session } }) => {
        if (session?.access_token) {
          let cookieStr = `rad_session=${session.access_token}; path=/; SameSite=Lax; Max-Age=${60 * 60 * 24}`
          cookieStr = appendCookieScope(cookieStr)
          document.cookie = cookieStr
          console.log(`AuthStore: Set rad_session cookie`);
        }
      });

      persistAccountSelection()
    } catch (err) {
      console.error('Error persisting auth:', err)
    }
  }

  // Clear persisted auth state
  const clearPersistedAuth = () => {
    console.log('AuthStore: Clearing persisted auth state')
    localStorage.removeItem('auth_user')
    localStorage.removeItem('auth_account')
    localStorage.removeItem('auth_account_users')
    localStorage.removeItem('auth_session_expiry')
    localStorage.removeItem('last_dashboard_route') // Also clear this to be safe
    sessionStorage.removeItem('rad_active_account_id')

    // Clear rad_session cookie
    let clearCookieStr = `rad_session=; path=/; Max-Age=0`
    clearCookieStr = appendCookieScope(clearCookieStr)
    document.cookie = clearCookieStr
    console.log(`AuthStore: Cleared session cookie and active account selection`);
  }

  const isAuthenticated = computed(() => !!user.value && !!session.value)
  const currentAccountUser = computed(() => accountUsers.value.find(au => au.account_id === activeAccountId.value))
  const accountRole = computed(() => currentAccountUser.value?.role)
  const isAdmin = computed(() => accountRole.value === 'admin')
  const isDev = computed(() => accountRole.value === 'dev')
  const isBotUser = computed(() => accountRole.value === 'botuser')

  async function signInWithProvider(provider: 'google' | 'discord' | 'slack' | 'github') {
    try {
      loading.value = true
      error.value = null

      // Store redirect URL if provided
      const urlParams = new URLSearchParams(window.location.search);
      const redirectUrl = urlParams.get('redirect') || urlParams.get('returnTo');
      if (redirectUrl) {
        sessionStorage.setItem('post_auth_redirect', redirectUrl);
      }

      const { data, error: signInError } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent select_account'
          }
        }
      })

      if (signInError) throw signInError
      return data
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Authentication failed'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function signOut() {
    console.log('AuthStore: Starting signOut process')
    try {
      loading.value = true
      console.log('AuthStore: Calling supabase.auth.signOut()')
      const { error: signOutError } = await supabase.auth.signOut()
      if (signOutError) {
        console.error('AuthStore: Supabase signOut error:', signOutError)
        throw signOutError
      }

      console.log('AuthStore: Supabase signOut successful, clearing local state')
      resetAuthState()

      console.log('AuthStore: SignOut process completed successfully')
    } catch (err) {
      console.error('AuthStore: SignOut failed:', err)
      error.value = err instanceof Error ? err.message : 'Sign out failed'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function loadUser() {
    try {
      console.log('loadUser: Attempting to get user session.');
      loading.value = true
      let { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (!sessionData?.session || sessionError) {
        // The server-side rad_session cookie can contain an expired access token while
        // Supabase still holds a refreshable browser session. Refresh before clearing
        // state so returnTo-based local-channel flows resume without a second attempt.
        const refreshed = await supabase.auth.refreshSession();
        sessionData = refreshed.data;
        sessionError = refreshed.error;
      }
      if (!sessionData?.session || sessionError) {
        console.log("loadUser - no refreshable local user session found");
        await handleMissingSession()
        return;
      }
      session.value = sessionData.session;

      
      
      

      const { data: { user: supabaseUser }, error: userError } = await supabase.auth.getUser()
      console.log('loadUser: supabase.auth.getUser() call completed.');
      if (userError) {
        console.error('Supabase getUser error:', userError);
        throw userError;
      }
      console.log('loadUser: Received supabaseUser:', supabaseUser);

      if (supabaseUser) {

        // ADMIN MODE LOCKDOWN CHECK
        if (import.meta.env.VITE_APP_MODE === 'admin') {
          const apiUrl = import.meta.env.VITE_API_URL || window.location.origin
          const response = await fetch(`${apiUrl}/api/admin/auth/verify`, {
            headers: {
              Authorization: `Bearer ${sessionData.session.access_token}`
            }
          })

          if (!response.ok) {
            console.error('Unauthorized Admin Access Attempt:', supabaseUser.email)
            showError('Access Denied: You do not have permission to access the Super Admin portal.')
            await supabase.auth.signOut()
            await handleMissingSession()
            return
          }
        }

        // Verify if the persisted user matches the actual user
        if (user.value && user.value.id !== supabaseUser.id) {
          console.warn('AuthStore: Persisted user ID mismatch, clearing stale state');
          activeAccountId.value = null;
          account.value = null;
          accountUsers.value = [];
        }

        console.log('loadUser: Bootstrapping user profile through API');
        const profileResponse = await fetch(`${import.meta.env.VITE_API_URL || window.location.origin}/api/auth/profile/bootstrap`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${sessionData.session.access_token}`,
            'Content-Type': 'application/json'
          }
        })

        if (!profileResponse.ok) {
          const errorBody = await profileResponse.json().catch(() => null) as { error?: string; message?: string } | null
          throw new Error(errorBody?.error || errorBody?.message || 'Failed to initialize user profile')
        }

        const dbUser = await profileResponse.json() as Database['public']['Tables']['users']['Row']
        user.value = {
          id: dbUser.id,
          email: dbUser.email,
          displayName: dbUser.display_name || undefined,
          avatarUrl: dbUser.cached_avatar_url || dbUser.avatar_url || undefined,
          provider: dbUser.provider,
          providerId: dbUser.provider_id
        }

        // Load user's account associations
        console.log('loadUser: Calling loadUserAccounts()');
        await loadUserAccounts()
        
        // Persist auth state to localStorage
        persistAuth()

        // Refresh cached avatar and check for free-tier loyalty reward
        refreshAvatarCache()
        checkLoyaltyReward()
      } else {
        console.log('loadUser: No supabaseUser found, resetting auth state.');
        await handleMissingSession()
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load user'
      console.error('Error loading user:', err)
    } finally {
      console.log('loadUser: Finally block executed, loading.value = false');
      loading.value = false
      initialized.value = true
      initializationPromise = null
    }
  }

  async function initializeAuth() {
    if (initialized.value) return

    // Restore this tab's account selection before validating current memberships.
    loadPersistedAccountSelection()

    if (!initializationPromise) {
      initializationPromise = loadUser()
    }
    await initializationPromise
  }

  async function loadUserAccounts() {
    if (!user.value) {
      console.log('loadUserAccounts: No user.value, returning.');
      return;
    }
    console.log('loadUserAccounts: Starting for user:', user.value.id);

    try {
      console.log("loadUserAccounts: Calling supabase.from('account_users').select()");
      const { data: userAccounts, error: accountsError } = await supabase
        .from('account_users')
        .select(`
          *,
          account:accounts(*),
          user:users!fk_user_id(*),
          invitedByUser:users!fk_invited_by(*)
        `)
        .eq('user_id', user.value.id)
        .eq('is_active', true)

      if (accountsError) {
        console.error('DB account_users select error:', accountsError);
        throw accountsError;
      }
      console.log('loadUserAccounts: Received userAccounts:', userAccounts);

      accountUsers.value = userAccounts;

      console.log('loadUserAccounts: Mapped accountUsers:', accountUsers.value);

      // Set primary account (first one or default)
      if (accountUsers.value.length > 0) {
        const targetAccountId = resolveActiveAccountId(accountUsers.value, activeAccountId.value)!;

        console.log('loadUserAccounts: Fetching account details for accountId:', targetAccountId);
        const { data: accountData, error: accountError } = await supabase
          .from('accounts')
          .select('*')
          .eq('id', targetAccountId)
          .single()

        if (accountError) {
          console.error('DB accounts select error:', accountError);
          throw accountError;
        }
        console.log('loadUserAccounts: Received accountData:', accountData);

        activeAccountId.value = targetAccountId;
        account.value = accountData;
        persistAccountSelection()
        console.log('loadUserAccounts: Set account:', account.value);

      } else {
        console.log('loadUserAccounts: No accountUsers found, clearing account.');
        activeAccountId.value = null;
        account.value = null;
        persistAccountSelection()
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load accounts'
      console.error('Error loading accounts:', err)
    }
  }

  async function switchAccount(accountId: string) {
    console.log('AuthStore: Switching to account:', accountId)
    const accountUser = accountUsers.value.find(au => au.account_id === accountId)
    if (!accountUser && accountId !== user.value?.id) {
      throw new Error('Account not found or access denied')
    }

    try {
      loading.value = true
      const { data: accountData, error: accountError } = await supabase
        .from('accounts')
        .select('*')
        .eq('id', accountId)
        .single()

      if (accountError) throw accountError

      activeAccountId.value = accountId
      account.value = accountData
      persistAccountSelection()

      window.dispatchEvent(new CustomEvent('account-switched', { detail: { accountId } }))
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to switch account'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function createAccount(name: string) {
    if (!user.value || !session.value?.access_token) throw new Error('User not authenticated')

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || window.location.origin}/api/accounts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.value.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name })
      })

      if (!response.ok) {
        const errorBody = await response.json().catch(() => null) as { error?: string; message?: string } | null
        throw new Error(errorBody?.error || errorBody?.message || 'Failed to create account')
      }

      const newAccount = await response.json() as Account
      await loadUserAccounts()
      return newAccount
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to create account'
      throw err
    }
  }

  async function refreshAvatarCache() {
    if (!session.value?.access_token || !user.value) {
      return
    }

    if (user.value.avatarUrl?.includes('/storage/v1/object/public/avatars/')) {
      return
    }

    if (avatarRefreshPromise) {
      return avatarRefreshPromise
    }

    avatarRefreshPromise = (async () => {
      try {
        const apiUrl = import.meta.env.VITE_API_URL || window.location.origin
        const response = await fetch(`${apiUrl}/api/auth/refresh-avatar`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.value.access_token}`,
            'Content-Type': 'application/json'
          }
        })

        if (!response.ok) {
          console.warn('refreshAvatarCache: Failed to refresh avatar cache', response.status)
          return
        }

        const data = await response.json()
        if (data.cachedAvatarUrl && user.value) {
          user.value = {
            ...user.value,
            avatarUrl: data.cachedAvatarUrl
          }
        }
      } catch (err) {
        console.error('Error refreshing avatar cache:', err)
      } finally {
        avatarRefreshPromise = null
      }
    })()

    return avatarRefreshPromise
  }

  async function checkLoyaltyReward() {
    if (!account.value || !session.value?.access_token) {
      console.log('checkLoyaltyReward: Missing account or session', { account: !!account.value, session: !!session.value });
      return
    }

    try {
      console.log('checkLoyaltyReward: Checking for reward for account:', account.value.id);
      const apiUrl = import.meta.env.VITE_API_URL || window.location.origin
      const response = await fetch(`${apiUrl}/api/billing/loyalty-reward`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.value.access_token}`,
          'X-Account-Id': account.value.id,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        console.log('checkLoyaltyReward: Response:', data);
      } else {
        console.warn('checkLoyaltyReward: Failed to check reward', response.status);
      }
    } catch (err) {
      console.error('Error checking loyalty reward:', err)
    }
  }

  // Listen for auth changes
  supabase.auth.onAuthStateChange((event, _session) => {
    console.log('Auth state change event:', event, _session)
    session.value = _session

    if (_session?.access_token) {
      let cookieStr = `rad_session=${_session.access_token}; path=/; SameSite=Lax; Max-Age=${60 * 60 * 24}`
      cookieStr = appendCookieScope(cookieStr)
      document.cookie = cookieStr
      console.log(`AuthStore: Synced rad_session cookie on ${event}`)
    }

    setTimeout(async () => {
      if (!_session && event === 'SIGNED_OUT') {
        console.log(`Auth state: ${event}, actively signed out`)
        await handleMissingSession()
        return
      }

      if (event === 'SIGNED_IN') {
        console.log('Auth state: SIGNED_IN')

        const redirectUrl = sessionStorage.getItem('post_auth_redirect')
        if (redirectUrl) {
          sessionStorage.removeItem('post_auth_redirect')
          window.location.href = redirectUrl
        }
      }
    }, 0)
  })

  return {
    user,
    activeAccountId,
    account,
    accountUsers,
    session,
    loading,
    error,
    initialized,
    isAuthenticated,
    accountRole,
    isAdmin,
    isDev,
    isBotUser,
    signInWithProvider,
    signOut,
    loadUser,
    initializeAuth,
    switchAccount,
    createAccount,
    checkLoyaltyReward
  }
})